import type { TerminalsController_register_v1Response } from '@repo/api-client';

import type { ShiftAdmitRefusalCode } from './roster.ts';

/**
 * The device's own identity, as the renderer is allowed to see it.
 *
 * A lease is a signed, 72-hour grant naming the outlet this device stands at.
 * It is the credential that makes a request from here a request *at that
 * outlet*, whatever role the person at the counter holds — so it lives in the
 * main process and never crosses into the renderer. What crosses is this:
 * status, not the secret.
 */

/**
 * The zone every time on a till is read in: Munnar's.
 *
 * Never the machine's. The tills ship on US Pacific, the Windows default, and
 * "today" began at 12:30 pm the day before until this was said outright (#331).
 */
export const PARK_TIME_ZONE = 'Asia/Kolkata';

/** Exactly what the API returned from `POST /v1/terminals/{id}/register`. */
export type TerminalRegistration = TerminalsController_register_v1Response;

/**
 * Why a registration was refused.
 *
 * Desktop-local codes, not the API's: every one of these is decided on the
 * device, against bytes the API has already sent. A screen maps them to words
 * the way `error-messages.ts` maps the API's codes; nothing here is copy.
 */
export type LeaseRejectionCode =
  | 'LEASE_MALFORMED'
  | 'LEASE_UNSUPPORTED_ALGORITHM'
  | 'LEASE_KEY_MISMATCH'
  | 'LEASE_SIGNATURE_INVALID'
  | 'LEASE_EXPIRED'
  /** A renewal named a terminal or an outlet other than the one this device is. */
  | 'LEASE_TERMINAL_MISMATCH'
  /** A registration replayed a lease older than the one this device already holds. */
  | 'LEASE_SUPERSEDED';

/**
 * Where a device stands with its lease.
 *
 * Four states rather than a boolean, because the person at the counter has to be
 * told a different thing in each and conflating them turns a broadband outage
 * into a suspected theft (FR-124c).
 *
 * Declared here, on the seam, rather than in the main process alone. It used to
 * live only in `lease-store.ts` and the renderer was handed `registered: true |
 * false` — which is the same word for a working till and for one that has been
 * withdrawn, and issue #215 is what that cost: a revoked terminal rendered the
 * ordinary sign-on screen, refused every PIN typed into it, and offered no way
 * to the one screen that could put it back into service.
 */
export type TerminalStatus = 'unregistered' | 'live' | 'expired' | 'revoked';

/**
 * The three a device that *holds* a registration can be in.
 *
 * Registration is a row, and status is what that row currently means. Keeping
 * them as two facts rather than one is what lets a screen say "withdrawn" and
 * "run out" differently while still knowing which terminal it is standing on —
 * the prefix and the outlet are on the row either way.
 */
export type RegisteredTerminalStatus = Exclude<TerminalStatus, 'unregistered'>;

export interface RegisteredTerminal {
  readonly registered: true;
  /**
   * Whether the registration on this device is one it may still act on.
   *
   * **`registered: true` is not permission to sell.** A revoked or expired
   * device is still registered — it knows its outlet and its prefix, and it is
   * still that terminal — and every route that takes money refuses it. A screen
   * that gates on `registered` alone gates on the wrong fact; gate on this.
   */
  readonly status: RegisteredTerminalStatus;
  /** The one outlet this device belongs to. Every write it makes is at this outlet. */
  readonly outletId: string;
  /** Printed on every ticket this device issues, and never reused or renumbered. */
  readonly prefix: string;
  /** ISO-8601, on the **server's** clock at issue. */
  readonly expiresAt: string;
}

export interface UnregisteredTerminal {
  readonly registered: false;
  /**
   * Stated rather than implied, so that a screen deciding what a device may do
   * reads one field across the whole union instead of a boolean here and a
   * status there.
   */
  readonly status: 'unregistered';
}

/**
 * A device that has never been registered is not a device in an error state.
 * The union says so, so a screen renders "not registered yet" rather than
 * having to read a thrown exception as a state.
 */
export type TerminalIdentity = RegisteredTerminal | UnregisteredTerminal;

/**
 * One of the registrations this device holds — issue #243.
 *
 * A device holds one registration per outlet it sells for, because the API
 * takes the outlet from the lease on every write: the gate PC that also sells
 * Glass Bridge tickets holds a Gate lease and a Glass Bridge lease, and the
 * counter sells under exactly one of them at a time.
 */
export interface HeldRegistration {
  /**
   * This device's own number for the registration, for choosing it. It means
   * nothing anywhere else — not a terminal id, not an outlet id.
   */
  readonly id: number;
  /** Whether the counter is selling under this one. At most one is. */
  readonly active: boolean;
  /**
   * What the outlet is called, as the back office knew it when a terminal was
   * last registered on this machine. `null` until then — nothing a terminal can
   * read tells it — and display text only: nothing is decided by it.
   */
  readonly outletName: string | null;
  readonly terminal: RegisteredTerminal;
  /** Whether this outlet asks for a visitor's time slot, and what its paper warns (#277). */
  readonly timeSlot: TimeSlotSetting;
  /**
   * Whether its held catalogue has any admission — a ticket type or an
   * activity — which is all Sell for can ring up from another outlet (#284).
   * `null` until it holds a catalogue at all. A shop selling goods only is
   * `false`, and is offered for switching to but not for Sell for (#313).
   */
  readonly sellsAdmission: boolean | null;
}

/**
 * A timed outlet's rule — the Glass Bridge admits visitors in windows, and its
 * paper prints the window and a warning (#277). Decided on the till from the
 * outlet's id, and on for the Glass Bridge alone (#325). Not an API field.
 */
export interface TimeSlotSetting {
  /** Ask for the window, from and to, before a sale is charged. */
  readonly ask: boolean;
  /** Printed under the window, as two lines where it does not fit on one. */
  readonly disclaimer: string;
}

/**
 * What the back office knows the park's outlets as, by outlet id, handed over
 * with a registration so the till can say where it is selling.
 *
 * Display text. The device keeps the names of the outlets it holds a
 * registration for and ignores the rest.
 */
export type OutletNames = Readonly<Record<string, string>>;

/**
 * The answer to choosing a registration to sell under.
 *
 * **Whoever is signed in stays signed in (#254)** — where the roster of the
 * outlet chosen has them on it, as this device holds it. Where it does not, or
 * holds none, the counter stays where it was and says why, in a shift refusal
 * code. `REGISTRATION_NOT_HELD` is an id this device does not hold.
 */
export type TerminalActivationOutcome =
  | { readonly activated: true }
  | {
      readonly activated: false;
      readonly reason: 'REGISTRATION_NOT_HELD' | ShiftAdmitRefusalCode;
    };

/**
 * The answer to handing the main process a registration.
 *
 * A refusal is a result rather than a thrown error: the renderer has to tell
 * the person standing at the device *which* refusal it was, and a rejected
 * promise carrying a stringified message is not something to branch on.
 */
export type TerminalRegistrationOutcome =
  | { readonly accepted: true; readonly identity: RegisteredTerminal }
  | { readonly accepted: false; readonly reason: LeaseRejectionCode };
